
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Receipt, Plus, Trash2, Upload } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DocumentContentEditor } from "./DocumentContentEditor";

interface InvoiceGeneratorViewProps {
  content: any;
  documentId: string;
  onUpdate: () => void;
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export function InvoiceGeneratorView({ content, documentId, onUpdate }: InvoiceGeneratorViewProps) {
  const [items, setItems] = useState<InvoiceItem[]>(content.items || []);
  const [logoUrl, setLogoUrl] = useState<string>(content.logoUrl || "");
  const { toast } = useToast();

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: "",
      quantity: 1,
      rate: 0,
      amount: 0
    };
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    updateContent({ items: updatedItems });
  };

  const updateItem = (id: string, field: keyof InvoiceItem, value: string | number) => {
    const updatedItems = items.map(item => {
      if (item.id === id) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'rate') {
          updatedItem.amount = updatedItem.quantity * updatedItem.rate;
        }
        return updatedItem;
      }
      return item;
    });
    setItems(updatedItems);
    updateContent({ items: updatedItems });
  };

  const removeItem = (id: string) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    updateContent({ items: updatedItems });
  };

  const updateContent = async (updates: any) => {
    try {
      const updatedContent = { ...content, ...updates };
      
      const { error } = await supabase
        .from("documents")
        .update({ 
          content: updatedContent,
          updated_at: new Date().toISOString()
        })
        .eq("id", documentId);

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast({
        title: "Error",
        description: "Failed to update invoice. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleContentUpdate = (field: string) => (newContent: string) => {
    updateContent({ [field]: newContent });
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + item.amount, 0);
  };

  const calculateTax = () => {
    const taxRate = content.taxRate || 0;
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/logo-${Date.now()}.${fileExt}`;

      const { data, error } = await supabase.storage
        .from('user-logos')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('user-logos')
        .getPublicUrl(fileName);

      setLogoUrl(publicUrl);
      updateContent({ logoUrl: publicUrl });

      await supabase.from('user_logos').insert({
        user_id: user.id,
        file_name: file.name,
        file_path: fileName,
        file_size: file.size,
        mime_type: file.type
      });

      toast({
        title: "Logo uploaded successfully",
        description: "Your logo has been added to the invoice template.",
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: "Upload failed",
        description: "Failed to upload logo. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <Receipt className="h-6 w-6 text-green-600" />
        <h2 className="text-2xl font-bold">Invoice Generator</h2>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label htmlFor="logo">Company Logo</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="flex-1"
                />
              </div>
              {logoUrl && (
                <div className="mt-2">
                  <img src={logoUrl} alt="Company Logo" className="h-16 w-auto" />
                </div>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="companyName">Company Name</Label>
              <Input
                id="companyName"
                placeholder="Your Company Name"
                value={content.companyName || ""}
                onChange={(e) => updateContent({ companyName: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="invoiceNumber">Invoice Number</Label>
              <Input
                id="invoiceNumber"
                placeholder="INV-001"
                value={content.invoiceNumber || ""}
                onChange={(e) => updateContent({ invoiceNumber: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <Label>Company Address</Label>
            <DocumentContentEditor
              section="Company Address"
              content={content.companyAddress || ""}
              documentId={documentId}
              field="companyAddress"
              onUpdate={handleContentUpdate("companyAddress")}
              showHint={true}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bill To</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="clientName">Client Name</Label>
            <Input
              id="clientName"
              placeholder="Client or Company Name"
              value={content.clientName || ""}
              onChange={(e) => updateContent({ clientName: e.target.value })}
            />
          </div>
          
          <div>
            <Label>Client Address</Label>
            <DocumentContentEditor
              section="Client Address"
              content={content.clientAddress || ""}
              documentId={documentId}
              field="clientAddress"
              onUpdate={handleContentUpdate("clientAddress")}
              showHint={true}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="invoiceDate">Invoice Date</Label>
              <Input
                id="invoiceDate"
                type="date"
                value={content.invoiceDate || ""}
                onChange={(e) => updateContent({ invoiceDate: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="dueDate">Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={content.dueDate || ""}
                onChange={(e) => updateContent({ dueDate: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Invoice Items</CardTitle>
            <Button onClick={addItem} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Item
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {items.map((item) => (
            <Card key={item.id} className="p-4">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-semibold">Item #{items.indexOf(item) + 1}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeItem(item.id)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor={`description-${item.id}`}>Description</Label>
                  <Input
                    id={`description-${item.id}`}
                    placeholder="Item description"
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                  />
                </div>
                
                <div>
                  <Label htmlFor={`quantity-${item.id}`}>Quantity</Label>
                  <Input
                    id={`quantity-${item.id}`}
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 1)}
                  />
                </div>
                
                <div>
                  <Label htmlFor={`rate-${item.id}`}>Rate</Label>
                  <Input
                    id={`rate-${item.id}`}
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.rate}
                    onChange={(e) => updateItem(item.id, 'rate', parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              
              <div className="mt-2 text-right">
                <span className="font-semibold">Amount: ${item.amount.toFixed(2)}</span>
              </div>
            </Card>
          ))}
          
          {items.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              No items added yet. Click "Add Item" to get started.
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invoice Totals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="taxRate">Tax Rate (%)</Label>
              <Input
                id="taxRate"
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={content.taxRate || 0}
                onChange={(e) => updateContent({ taxRate: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
          
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${calculateSubtotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax:</span>
              <span>${calculateTax().toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg">
              <span>Total:</span>
              <span>${calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Additional Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <DocumentContentEditor
                section="Notes"
                content={content.notes || ""}
                documentId={documentId}
                field="notes"
                onUpdate={handleContentUpdate("notes")}
                showHint={true}
              />
            </div>
            
            <div>
              <Label>Payment Terms</Label>
              <DocumentContentEditor
                section="Payment Terms"
                content={content.paymentTerms || ""}
                documentId={documentId}
                field="paymentTerms"
                onUpdate={handleContentUpdate("paymentTerms")}
                showHint={true}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
